import { Koi } from '@ksyos/koi';
import { FinanceSearchField } from '@common/enums/financeSearchField';
import { csProcessIdRegex, numberRegex } from '@common/utils/regularExpressions';
import type { ISchemaContainer } from '../../common/validation/schemaContainer';
import type { BatchRunValidated } from './batchRun';
import { batchRunSchema } from './batchRun';
import { replaceKoiError } from './replaceKoiError';

const searchTextSchema = Koi.alternatives().conditional('searchField', [
    {
        is: FinanceSearchField.processNumber,
        then: Koi.string()
            .pattern(csProcessIdRegex)
            .error((errors) => replaceKoiError(errors, 'string.pattern.base', 'string_numeric')),
    },
    {
        is: FinanceSearchField.relationNumber,
        then: Koi.string()
            .pattern(numberRegex)
            .error((errors) => replaceKoiError(errors, 'string.pattern.base', 'string_numeric')),
    },
    {
        is: FinanceSearchField.requesterPractitionerUid,
        then: Koi.string()
            .pattern(numberRegex)
            .error((errors) => replaceKoiError(errors, 'string.pattern.base', 'string_numeric')),
    },
    {
        is: Koi.valid(FinanceSearchField.organizationName, FinanceSearchField.groupName).required(),
        otherwise: Koi.string().max(255).optional(),
        then: Koi.string().max(255).required(),
    },
]);

export const financingFlexSearchFilterSchemaKeys = {
    batchRun: batchRunSchema.optional(),
    endDate: Koi.when('startDate', {
        is: Koi.exist(),
        otherwise: Koi.koi().date(),
        then: Koi.koi().date().endDate(),
    }).optional(),
    episodeCategoryId: Koi.string().uuid().optional(),
    searchField: Koi.when('searchText', {
        is: Koi.exist(),
        otherwise: Koi.koi().enum(FinanceSearchField).optional(),
        then: Koi.koi().enum(FinanceSearchField).required(),
    }),
    searchText: searchTextSchema,
    serviceId: Koi.string().uuid().optional(),
    startDate: Koi.koi().date().optional(),
};

export const financingFlexSearchFilterSchema = Koi.object(financingFlexSearchFilterSchemaKeys);

export const financingFlexSearchFilterValidationSchema: ISchemaContainer<FinancingFlexSearchFilterValidated> = {
    schema: financingFlexSearchFilterSchema,
};

export const financingFlexFilterSchema = financingFlexSearchFilterSchema.append({
    paging: Koi.object({
        limit: Koi.number().integer().min(1).max(100),
        offset: Koi.number().integer().min(0),
    }),
});

export type FinancingFlexSearchFilterValidated = {
    batchRun?: BatchRunValidated;
    endDate?: string;
    episodeCategoryId?: string;
    searchField?: FinanceSearchField;
    searchText?: string;
    serviceId?: string;
    startDate?: string;
};

export type FinancingFlexFilterValidated = FinancingFlexSearchFilterValidated & {
    paging: {
        limit: number;
        offset: number;
    };
};

export type FinancingFlexReprocessValidated = FinancingFlexSearchFilterValidated & {
    processNumbers?: number[];
};

export const financingFlexReprocessSchema = financingFlexSearchFilterSchema.append({
    processNumbers: Koi.array().items(Koi.number().min(1)).optional(),
});
